import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { IonCol, IonRow } from '@ionic/angular/standalone';
import {
  Editor,
  NgxEditorComponent,
  NgxEditorMenuComponent,
  Toolbar,
} from 'ngx-editor';
import { finalize } from 'rxjs';
import {
  HomeMessage,
  MESSAGE_TYPE,
  MessageForm,
  MessageModalInput,
} from 'src/app/core/models/home/message.model';
import { HomeMessageService } from 'src/app/core/services/home/home-message.service';
import { BUTTON_TYPES } from '../../shared/modal/components/modal-frame/modal-frame.constants';
import { SharedModule } from '../../shared/shared.module';
import {
  DISPLAY_OPTIONS,
  EDIT_MESSAGE_CONSTS,
} from './edit-message-modal.constants';

@Component({
  selector: 'app-edit-message-modal',
  imports: [
    NgxEditorComponent,
    NgxEditorMenuComponent,
    FormsModule,
    IonRow,
    IonCol,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './edit-message-modal.component.html',
  styleUrl: './edit-message-modal.component.scss',
})
export class EditMessageModalComponent implements OnInit, OnDestroy {
  inputData = inject<MessageModalInput>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<EditMessageModalComponent>);
  private homeService = inject(HomeMessageService);

  readonly DISPLAY_OPTIONS = DISPLAY_OPTIONS;
  readonly CONSTS = EDIT_MESSAGE_CONSTS;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  headerEditor!: Editor;
  bodyEditor!: Editor;
  customToolbar: Toolbar = [
    ['bold', 'italic', 'underline', 'bullet_list', 'ordered_list', 'link'],
  ];
  defaultTime = '';
  defaultDate = new Date();
  messageForm!: FormGroup<MessageForm>;

  ngOnInit(): void {
    this.headerEditor = new Editor();
    this.bodyEditor = new Editor();
    this.messageForm = this.initializeForm(this.inputData.data);
  }

  ngOnDestroy(): void {
    this.headerEditor.destroy();
    this.bodyEditor.destroy();
  }

  initializeForm(
    defaultValues: HomeMessage | undefined,
  ): FormGroup<MessageForm> {
    const start = defaultValues?.start ?? new Date();
    const end = defaultValues?.start ?? new Date();
    return new FormGroup<MessageForm>({
      id: new FormControl(defaultValues?.id ?? null),
      headerText: new FormControl(defaultValues?.header ?? '', {
        validators: Validators.required,
        nonNullable: true,
      }),
      bodyText: new FormControl(defaultValues?.body ?? '', {
        validators: Validators.required,
        nonNullable: true,
      }),
      hasDateTime: new FormControl(true, { nonNullable: true }),
      displayType: new FormControl(
        defaultValues?.displayType
          ? DISPLAY_OPTIONS[defaultValues.displayType]
          : DISPLAY_OPTIONS[0],
        { nonNullable: true },
      ),
      startDate: new FormControl(start ?? new Date(), { nonNullable: true }),
      endDate: new FormControl(end ?? new Date(), { nonNullable: true }),
      startTime: new FormControl(`${start.getHours()}:${start.getMinutes()}`, {
        nonNullable: true,
        validators: [Validators.minLength(5)],
      }),
      endTime: new FormControl(`${end.getHours()}:${end.getMinutes()}`, {
        nonNullable: true,
        validators: [Validators.minLength(5)],
      }),
    });
  }

  toggleDateTime() {
    if (this.messageForm.get('hasDateTime')?.value) {
      this.messageForm.get('startDate')?.disable();
      this.messageForm.get('endDate')?.disable();
      this.messageForm.get('startTime')?.disable();
      this.messageForm.get('endTime')?.disable();
    } else {
      this.messageForm.get('startDate')?.enable();
      this.messageForm.get('endDate')?.enable();
      this.messageForm.get('startTime')?.enable();
      this.messageForm.get('endTime')?.enable();
    }
  }

  clickExit() {
    this.dialog.closeAll();
  }

  clickSave() {
    const formattedForm: HomeMessage = {
      id: this.messageForm.get('id')?.value || null,
      header: this.messageForm.get('headerText')?.value || '',
      body: this.messageForm.get('bodyText')?.value || '',
      displayType:
        (this.messageForm.get('displayType')?.value.value as MESSAGE_TYPE) ||
        MESSAGE_TYPE.INFO,
      start: this.messageForm.get('hasDateTime')?.value
        ? this.formatDate(
            this.messageForm.get('startDate')?.value ?? new Date(),
            this.messageForm.get('startTime')?.value ?? '00:00',
          )
        : null,
      end: this.messageForm.get('hasDateTime')?.value
        ? this.formatDate(
            this.messageForm.get('endDate')?.value ?? new Date(),
            this.messageForm.get('endTime')?.value ?? '00:00',
          )
        : null,
    };

    if (this.inputData?.edit) {
      this.homeService
        .editMessage(formattedForm)
        .pipe(
          finalize(() => {
            this.dialogRef.close(true);
          }),
        )
        .subscribe();
    } else {
      this.homeService
        .addNewMessage(formattedForm)
        .pipe(
          finalize(() => {
            this.dialogRef.close(true);
          }),
        )
        .subscribe();
    }
  }

  private formatDate(date: Date, time: string) {
    const newDate = date;
    const startTime = time?.split(':');
    date.setHours(Number.parseInt(startTime[0]));
    date.setMinutes(Number.parseInt(startTime[1]));
    return newDate;
  }
}
